import { getFormStoreSingleton } from '../../store/store';
import { FormStateGetters } from '../../store/types';
import { BaseValidationError } from '../../types';

import { assertFormInitialized } from './assertFormInitialized';

/**
 * Создает геттеры состояния формы – объект `formInstance.state`
 *
 * @param form Название и идентификатор формы
 * @param getFormInitializationId Возвращает актуальный id инициализации формы на момент вызова геттера
 */
export function createFormStateGetters<FormValue, FormValidationError extends BaseValidationError>(
    form: { name: string; id: string },
    getFormInitializationId: () => string
): FormStateGetters<FormValue, FormValidationError> {
    const {
        getFormIsSubmitting,
        getFormIsTouched,
        getFormIsDirty,
        getFormIsValid,
        getFormIsActionsRunning,
        getFormIsAwaitingStartedAsyncSetters,
        getFormCanSubmit,
        getFormErrors,
        getFormErrorsMap,
        getFormSubmitErrors,
        getFormInitialValue,
        getFormAsyncActionsMeta,
    } = getFormStoreSingleton(form.name);

    function getParams() {
        const formInitializationId = getFormInitializationId();

        assertFormInitialized({ formName: form.name, formId: form.id, formInitializationId });

        return { formInitializationId };
    }

    return {
        canSubmit: () => getFormCanSubmit(getParams()),
        isValid: () => getFormIsValid(getParams()),
        isSubmitting: () => getFormIsSubmitting(getParams()),
        isAwaitingStartedAsyncSetters: () => getFormIsAwaitingStartedAsyncSetters(getParams()),
        isTouched: () => getFormIsTouched(getParams()),
        isDirty: () => getFormIsDirty(getParams()),
        isActionsRunning: () => getFormIsActionsRunning(getParams()),
        // Ошибки в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        errors: () => getFormErrors(getParams()) as FormValidationError[],
        errorsMap: () =>
            // Ошибки в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            (getFormErrorsMap(getParams()) ?? { onChange: [], onBlur: [], onSubmit: [] }) as ReturnType<
                FormStateGetters<FormValue, FormValidationError>['errorsMap']
            >,
        submitErrors: () => getFormSubmitErrors(getParams()),
        // Значения в сторе не типизированы, но мы гарантируем тип публичным интерфейсом инстанса
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        initialValue: () => getFormInitialValue(getParams()) as FormValue,
        asyncActionsMeta: () => getFormAsyncActionsMeta(getParams()) ?? {},
    };
}
